import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, Mic, MicOff } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	trackOnboardingMicSetupCompleted,
	trackOnboardingMicSetupSkipped,
	trackOnboardingModeSelected,
} from "@/lib/analytics-events";

export const Route = createFileRoute("/_app/_/onboarding/mic-setup")({
	component: MicSetupPage,
});

type MicState = "pending" | "denied" | "ready";

function MicSetupPage() {
	const navigate = useNavigate();
	const [micState, setMicState] = useState<MicState>("pending");
	const [audioLevels, setAudioLevels] = useState<number[]>(
		new Array(20).fill(0),
	);
	const streamRef = useRef<MediaStream | null>(null);
	const analyserRef = useRef<AnalyserNode | null>(null);
	const animFrameRef = useRef<number>(0);

	const startAudioAnalysis = useCallback((stream: MediaStream) => {
		const audioCtx = new AudioContext();
		const source = audioCtx.createMediaStreamSource(stream);
		const analyser = audioCtx.createAnalyser();
		analyser.fftSize = 64;
		source.connect(analyser);
		analyserRef.current = analyser;

		const dataArray = new Uint8Array(analyser.frequencyBinCount);

		const update = () => {
			analyser.getByteFrequencyData(dataArray);
			const levels: number[] = [];
			const step = Math.floor(dataArray.length / 20);
			for (let i = 0; i < 20; i++) {
				levels.push(dataArray[i * step] / 255);
			}
			setAudioLevels(levels);
			animFrameRef.current = requestAnimationFrame(update);
		};
		update();
	}, []);

	const requestMic = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia({
				audio: true,
			});
			streamRef.current = stream;
			setMicState("ready");
			startAudioAnalysis(stream);
		} catch {
			setMicState("denied");
		}
	};

	useEffect(() => {
		return () => {
			cancelAnimationFrame(animFrameRef.current);
			streamRef.current?.getTracks().forEach((t) => t.stop());
		};
	}, []);

	const handleSkip = () => {
		localStorage.setItem("helm_onboarding_mode", "text");
		trackOnboardingModeSelected("text");
		trackOnboardingMicSetupSkipped();
		navigate({ to: "/interview/session" });
	};

	const handleStart = () => {
		streamRef.current?.getTracks().forEach((t) => t.stop());
		cancelAnimationFrame(animFrameRef.current);
		trackOnboardingModeSelected("voice");
		trackOnboardingMicSetupCompleted();
		navigate({ to: "/interview/session" });
	};

	return (
		<div className="h-dvh flex flex-col items-center justify-center px-6">
			<div className="w-full max-w-md space-y-6 text-center">
				{micState === "pending" && (
					<>
						<div className="h-20 w-20 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
							<Mic className="h-10 w-10 text-primary" />
						</div>
						<div className="space-y-2">
							<h1 className="text-2xl font-semibold text-foreground">
								Let's test your microphone
							</h1>
							<p className="text-muted-foreground">
								We need microphone access for voice conversations with Xander.
							</p>
						</div>
						<div className="flex flex-col items-center gap-3">
							<Button
								size="lg"
								className="h-12 w-full max-w-xs"
								onClick={requestMic}
							>
								Enable Microphone
							</Button>
							<button
								type="button"
								onClick={handleSkip}
								className="text-sm text-muted-foreground hover:text-foreground underline"
							>
								Type my answers instead
							</button>
						</div>
					</>
				)}

				{micState === "denied" && (
					<>
						<div className="h-20 w-20 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
							<MicOff className="h-10 w-10 text-destructive" />
						</div>
						<div className="space-y-2">
							<h1 className="text-2xl font-semibold text-foreground">
								Microphone access denied
							</h1>
							<p className="text-muted-foreground">
								To use voice mode, enable microphone access in your browser
								settings and reload the page.
							</p>
						</div>
						<Button
							variant="outline"
							size="lg"
							className="h-12 w-full max-w-xs"
							onClick={handleSkip}
						>
							Type my answers instead
						</Button>
					</>
				)}

				{micState === "ready" && (
					<>
						<div className="h-20 w-20 mx-auto rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
							<Check className="h-10 w-10 text-green-600 dark:text-green-400" />
						</div>
						<div className="space-y-2">
							<h1 className="text-2xl font-semibold text-foreground">
								Microphone is ready
							</h1>
							<p className="text-muted-foreground">
								Speak into your microphone to see the audio levels below.
							</p>
						</div>
						{/* Audio level visualizer */}
						<div className="flex items-end justify-center gap-1 h-16">
							{audioLevels.map((level, i) => (
								<div
									key={i}
									className="w-2 rounded-full bg-primary transition-all duration-75"
									style={{
										height: `${Math.max(4, level * 64)}px`,
										opacity: 0.4 + level * 0.6,
									}}
								/>
							))}
						</div>
						<div className="space-y-3">
							<Button
								size="lg"
								className="h-12 w-full max-w-xs"
								onClick={handleStart}
							>
								Start Interview
							</Button>
							<p className="text-xs text-muted-foreground">
								Pro tip: Use headphones for the best experience
							</p>
						</div>
					</>
				)}
			</div>
		</div>
	);
}
